import { Box, Button, ProgressBar, Typography } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { startCase } from "lodash";
import React from "react";

import {
  QUIZ_GENERATION_PROGRESS_CAP,
  QUIZ_GENERATION_TOTAL_STEPS,
  QUIZ_WRITER_CARD_BACKGROUND_GRADIENT,
} from "./consts";
import { QuizGenerateLaunchOptions } from "./useQuizGeneratorWebsocket";
import { QuizWriterUnlockedTopic } from "./useQuizTopicGeneratorWebsocket";

interface QuizWriterResultsViewProps {
  score: number;
  questionCount: number;
  topicDisplayName: string;
  unlockedTopics: QuizWriterUnlockedTopic[] | null;
  isTopicsGenerating: boolean;
  topicsGenerationLogStepCount: number;
  latestTopicGenUserLogMessage: string | null;
  topicsGenError: string | null;
  didYouKnowFact: string;
  onGenerate: (launch: QuizGenerateLaunchOptions) => void;
  onResetToQuizStartForm: () => void;
  onOpenMyTopicsDrawer: () => void;
}

export function QuizWriterResultsView({
  score,
  questionCount,
  topicDisplayName,
  unlockedTopics,
  isTopicsGenerating,
  topicsGenerationLogStepCount,
  latestTopicGenUserLogMessage,
  topicsGenError,
  didYouKnowFact,
  onGenerate,
  onResetToQuizStartForm,
  onOpenMyTopicsDrawer,
}: QuizWriterResultsViewProps) {
  const { t } = useTranslation("learnerApp\\Quiz");

  return (
    <>
      <Box
        sx={{
          overflow: "hidden",
          borderRadius: "20px",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
          border: "1px solid",
          borderColor: "grey.100",
          backgroundColor: "common.white",
        }}
      >
        <Box
          sx={{
            background: QUIZ_WRITER_CARD_BACKGROUND_GRADIENT,
            padding: { xs: "32px", sm: "40px" },
            textAlign: "center",
          }}
        >
          <Typography
            component="div"
            sx={{ fontSize: "3rem", lineHeight: 1, marginBottom: "16px" }}
            aria-hidden
          >
            🎉
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: "common.white",
              marginBottom: "8px",
            }}
          >
            {score === questionCount
              ? t("Perfect score!")
              : score >= questionCount * 0.7
              ? t("Great job!")
              : t("Keep it up!")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "rgba(255,255,255,0.9)",
              fontWeight: 500,
              fontSize: "1.5rem",
            }}
          >
            {t("You got {{score}} of {{total}} on {{topic}}", {
              score,
              total: questionCount,
              topic: topicDisplayName.trim() || t("Quiz"),
            })}
          </Typography>
        </Box>
        <Box
          sx={{
            padding: { xs: "20px", sm: "28px" },
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {unlockedTopics !== null && unlockedTopics.length > 0 ? (
            <UnlockedTopics
              unlockedTopics={unlockedTopics}
              onGenerate={onGenerate}
              onResetToQuizStartForm={onResetToQuizStartForm}
            />
          ) : (
            <TopicGenerationStatus
              isTopicsGenerating={isTopicsGenerating}
              topicsGenerationLogStepCount={topicsGenerationLogStepCount}
              latestTopicGenUserLogMessage={latestTopicGenUserLogMessage}
              topicsGenError={topicsGenError}
              didYouKnowFact={didYouKnowFact}
            />
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20px",
          marginBottom: "8px",
        }}
      >
        <Typography
          component="button"
          type="button"
          variant="body2"
          onClick={onOpenMyTopicsDrawer}
          sx={{
            border: 0,
            background: "none",
            padding: 0,
            cursor: "pointer",
            color: "text.secondary",
            fontSize: "1.5rem",
            fontWeight: 500,
            textDecoration: "underline",
            fontFamily: "inherit",
            textAlign: "center",
          }}
        >
          {t("or browse My Topics →")}
        </Typography>
      </Box>
    </>
  );
}

function UnlockedTopics({
  unlockedTopics,
  onGenerate,
  onResetToQuizStartForm,
}: {
  unlockedTopics: QuizWriterUnlockedTopic[];
  onGenerate: (launch: QuizGenerateLaunchOptions) => void;
  onResetToQuizStartForm: () => void;
}) {
  const { t } = useTranslation("learnerApp\\Quiz");

  return (
    <>
      <Box sx={{ marginBottom: "4px" }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            color: "text.primary",
            fontSize: "1.5rem",
          }}
        >
          {t("{{count}} topic unlocked", {
            count: unlockedTopics.length,
            defaultValue_plural: "{{count}} topics unlocked",
          })}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: "12px",
        }}
      >
        {unlockedTopics.map(topic => (
          <Box
            key={topic.uid}
            sx={{
              border: "1px solid",
              borderColor: topic.source === "quiz" ? "primary.200" : "grey.200",
              borderRadius: "16px",
              padding: { xs: "16px", sm: "18px" },
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "12px",
              minHeight: "124px",
            }}
          >
            <Typography
              component="div"
              sx={{
                borderRadius: "5px",
                backgroundColor:
                  topic.source === "quiz" ? "#E0F2FE" : "#DCFCE7",
                color: topic.source === "quiz" ? "#075985" : "#166534",
                fontSize: "1.1rem",
                fontWeight: 700,
                lineHeight: 1,
                padding: "5px 8px",
              }}
            >
              {topic.source === "quiz" ? t("Related") : t("Your interests")}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "text.primary",
                fontWeight: 700,
                fontSize: "1.45rem",
                lineHeight: 1.25,
              }}
            >
              {startCase(topic.name)}
            </Typography>
            <Button
              onClick={() =>
                onGenerate({
                  topicUid: topic.uid,
                  topicName: startCase(topic.name),
                })
              }
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              sx={{
                borderRadius: "999px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "1.35rem",
                padding: "10px 22px",
                boxShadow: "none",
                marginTop: "auto",
                "&:hover": {
                  boxShadow: "none",
                },
              }}
            >
              {t("Start")}
            </Button>
          </Box>
        ))}
      </Box>
      <Button
        onClick={onResetToQuizStartForm}
        variant="outlined"
        size="large"
        sx={{
          borderRadius: "999px",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "1.5rem",
          padding: "12px 24px",
          borderColor: "grey.300",
          color: "text.primary",
          "&:hover": {
            borderColor: "grey.400",
            backgroundColor: "grey.50",
          },
        }}
      >
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {t("Write another quiz")}
          <Box component="span" sx={{ fontSize: "1.5rem", lineHeight: 1 }}>
            →
          </Box>
        </Box>
      </Button>
    </>
  );
}

function TopicGenerationStatus({
  isTopicsGenerating,
  topicsGenerationLogStepCount,
  latestTopicGenUserLogMessage,
  topicsGenError,
  didYouKnowFact,
}: {
  isTopicsGenerating: boolean;
  topicsGenerationLogStepCount: number;
  latestTopicGenUserLogMessage: string | null;
  topicsGenError: string | null;
  didYouKnowFact: string;
}) {
  return (
    <>
      {isTopicsGenerating ? (
        <TopicsGeneratingCard
          topicsGenerationLogStepCount={topicsGenerationLogStepCount}
          latestTopicGenUserLogMessage={latestTopicGenUserLogMessage}
          didYouKnowFact={didYouKnowFact}
        />
      ) : null}
      {topicsGenError ? (
        <Box
          sx={{
            padding: "16px",
            backgroundColor: "error.50",
            borderRadius: "12px",
            border: "1px solid",
            borderColor: "error.200",
          }}
        >
          <Typography
            variant="body1"
            sx={{ color: "error.main", fontSize: "1.3rem" }}
          >
            {topicsGenError}
          </Typography>
        </Box>
      ) : null}
    </>
  );
}

function TopicsGeneratingCard({
  topicsGenerationLogStepCount,
  latestTopicGenUserLogMessage,
  didYouKnowFact,
}: {
  topicsGenerationLogStepCount: number;
  latestTopicGenUserLogMessage: string | null;
  didYouKnowFact: string;
}) {
  const { t } = useTranslation("learnerApp\\Quiz");
  const currentStep = Math.min(
    topicsGenerationLogStepCount,
    QUIZ_GENERATION_PROGRESS_CAP,
  );

  return (
    <Box
      sx={{
        borderRadius: "16px",
        border: "1px solid",
        borderColor: "grey.100",
        overflow: "hidden",
        marginBottom: "8px",
      }}
    >
      <Box sx={{ padding: { xs: "16px", sm: "20px" } }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            marginBottom: "4px",
          }}
        >
          {t("Unlocking new topics")}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            fontSize: "1.35rem",
          }}
        >
          {t(
            "Hang tight — we’re creating new quiz ideas from your recent answers.",
          )}
        </Typography>
      </Box>
      <Box sx={{ height: "1px", backgroundColor: "grey.200", opacity: 0.6 }} />
      <Box
        sx={{
          padding: { xs: "20px", sm: "24px" },
          textAlign: "center",
        }}
      >
        <Typography
          component="div"
          sx={{
            fontSize: "2rem",
            lineHeight: 1,
            marginBottom: "12px",
          }}
          aria-hidden
        >
          ✨
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            marginBottom: "16px",
            fontSize: "1.4rem",
          }}
        >
          {t("Preparing your new topics...")}
        </Typography>
        <ProgressBar
          variant="determinate"
          value={(currentStep / QUIZ_GENERATION_TOTAL_STEPS) * 100}
          sx={{
            height: 10,
            borderRadius: 999,
            maxWidth: 440,
            margin: "0 auto",
            backgroundColor: "#4CAF50",
            "& .MuiLinearProgress-bar": {
              borderRadius: 999,
              backgroundColor: "#4CAF50",
            },
          }}
        />
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            marginTop: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
            fontSize: "1.2rem",
          }}
        >
          <Box component="span" sx={{ color: "success.main" }}>
            ✓
          </Box>{" "}
          {t("{{current}} of {{total}} steps done", {
            current: currentStep,
            total: QUIZ_GENERATION_TOTAL_STEPS,
          })}
        </Typography>
        <Box
          sx={{
            marginTop: "16px",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            gap: "8px",
            paddingX: { xs: 0, sm: "8px" },
          }}
        >
          <Typography
            component="span"
            sx={{ fontSize: "1.15rem", lineHeight: 1.4 }}
            aria-hidden
          >
            🔍
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              textAlign: "left",
              flex: 1,
              maxWidth: 400,
              fontSize: "1.35rem",
            }}
          >
            {latestTopicGenUserLogMessage ?? t("Starting up…")}
            <Box
              component="span"
              sx={{
                marginLeft: "4px",
                color: "grey.400",
                letterSpacing: 3,
                "@keyframes quizWriterPulse": {
                  "0%, 100%": { opacity: 0.35 },
                  "50%": { opacity: 1 },
                },
                animation: "quizWriterPulse 1.2s ease-in-out infinite",
              }}
            >
              ···
            </Box>
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          padding: { xs: "16px", sm: "20px" },
          paddingTop: 0,
        }}
      >
        <Box
          sx={{
            borderRadius: "14px",
            backgroundColor: "#FFFBEB",
            padding: "14px",
            textAlign: "left",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: "#92400E",
              letterSpacing: "0.06em",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              marginBottom: "6px",
              fontSize: "1.1rem",
            }}
          >
            <span aria-hidden>💡</span> {t("DID YOU KNOW?")}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#1C1917",
              lineHeight: 1.55,
              fontSize: "1.3rem",
            }}
          >
            {didYouKnowFact}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
